import type { PrivateLessonBookingHistoryDTO } from "@/modules/students/domain/repo/list-private-lesson-bookings";
import { formatDate } from "@/shared/lib/date-time";
import { formatSlotDurationLaoLabel } from "@/shared/lib/private-lesson-slot-duration";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";

type PrivateLessonBookingRow = PrivateLessonBookingHistoryDTO;

type PrivateLessonBookingHistoryTableProps = {
  data: PrivateLessonBookingRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, _limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetails?: (booking: PrivateLessonBookingRow) => void;
};

export function PrivateLessonBookingHistoryTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetails,
}: PrivateLessonBookingHistoryTableProps) {
  const columns: TanstackReactTable.ColumnDef<PrivateLessonBookingRow>[] = [
    createSortableColumn("date", "ວັນທີ", {
      cell: ({ row }) => formatDate(row.original.date),
    }),
    {
      header: "ເວລາ",
      accessorKey: "startTime",
      cell: ({ row }) => {
        const startTime = row.original.startTime;
        const endTime = row.original.endTime;
        return (
          <div>
            <div className="font-semibold">{startTime}</div>
            <div className="text-muted-foreground text-xs">ຫາ {endTime}</div>
          </div>
        );
      },
    },
    {
      header: "ລະດັບເວລາ",
      accessorKey: "durationMinutes",
      cell: ({ row }) => {
        const minutes = row.original.durationMinutes;
        return formatSlotDurationLaoLabel(minutes) ?? "-";
      },
    },
    {
      header: "ຄູສອນ",
      accessorKey: "instructorNames",
      cell: ({ row }) => (row.original.instructorNames ?? []).join(", ") || "-",
    },
    {
      header: "ຫ້ອງ",
      accessorKey: "roomName",
      cell: ({ row }) => row.original.roomName || "-",
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "scheduled"
            ? "default"
            : status === "completed"
              ? "secondary"
              : "destructive";
        const label =
          status === "scheduled"
            ? "ຈອງແລ້ວ"
            : status === "completed"
              ? "ຮຽນແລ້ວ"
              : "ຍົກເລີກ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      header: "ແພັກເກດ",
      accessorKey: "templateName",
      cell: ({ row }) => (
        <span className="font-semibold text-destructive">
          {row.original.templateName}
        </span>
      ),
    },
    {
      header: "ໝາຍເຫດ",
      accessorKey: "notes",
      cell: ({ row }) => row.original.notes || "-",
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: PrivateLessonBookingRow } }) => {
        if (!onViewDetails) return null;
        return (
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => onViewDetails(row.original)}
              className="text-muted-foreground hover:text-foreground"
            >
              <EyeIcon className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable<PrivateLessonBookingRow, unknown>
      noDataMessage="ບໍ່ພົບປະຫວັດການຈອງຮຽນສ່ວນຕົວ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
